import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { confirmClientInvite } from "@/utils/client-invites.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/clients/invite/confirm/$token")({
  component: ConfirmInvitePage,
  head: () => ({
    meta: [{ title: "Signup confirmation" }],
  }),
});

type ConfirmState =
  | { status: "loading" }
  | { status: "success"; alreadyConfirmed: boolean; companyName?: string }
  | { status: "error"; message: string };

function ConfirmInvitePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const confirm = useServerFn(confirmClientInvite);
  const [state, setState] = useState<ConfirmState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    const errorMessage = (code: string): string => {
      switch (code) {
        case "invalid_token":
          return t("publicInviteConfirm.errors.invalid_token");
        case "expired":
          return t("publicInviteConfirm.errors.expired");
        case "not_submitted":
          return t("publicInviteConfirm.errors.not_submitted");
        default:
          return t("publicInviteConfirm.errors.default");
      }
    };
    (async () => {
      try {
        const res = await confirm({ data: { confirm_token: token } });
        if (cancelled) return;
        if (!res.ok) {
          setState({
            status: "error",
            message: errorMessage(res.error || "confirm_failed"),
          });
          return;
        }
        setState({
          status: "success",
          alreadyConfirmed: !!res.already_confirmed,
          companyName: res.company_name,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: "error",
          message: e instanceof Error ? e.message : t("publicInviteConfirm.errors.unexpected"),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-12">
      <div className="mx-auto max-w-lg">
        <Card>
          {state.status === "loading" && (
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t("publicInviteConfirm.confirming")}</p>
            </CardContent>
          )}

          {state.status === "success" && (
            <>
              <CardHeader className="items-center text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>
                  {state.alreadyConfirmed
                    ? t("publicInviteConfirm.alreadyConfirmed")
                    : t("publicInviteConfirm.confirmed")}
                </CardTitle>
                <CardDescription>
                  {state.companyName
                    ? t("publicInviteConfirm.withCompany", { company: state.companyName })
                    : t("publicInviteConfirm.withoutCompany")}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center pb-8">
                <Button asChild variant="outline">
                  <Link to="/">{t("publicInviteConfirm.backHome")}</Link>
                </Button>
              </CardContent>
            </>
          )}

          {state.status === "error" && (
            <>
              <CardHeader className="items-center text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <CardTitle>{t("publicInviteConfirm.cantConfirm")}</CardTitle>
                <CardDescription>{state.message}</CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center pb-8">
                <Button asChild variant="outline">
                  <Link to="/">{t("publicInviteConfirm.backHome")}</Link>
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
